import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import ErrorBanner from "@components/shared/ErrorBanner";
import Table, {
  TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
} from "@components/ui/Table";
import { fetchQueue } from "@services/reactivation";
import {
  CONTACT_STATUS, OUTCOME_LABELS, formatWireTime,
} from "@components/scheduling/campaignLabels";

const SOURCE_LABELS = { campaign: "campaign call", manual: "manual call" };

/** "Voicemail · Sep 14, 8:00 AM UTC-04:00 (campaign call)" or a dash. */
function LastContact({ value }) {
  if (!value?.at) return <span className="text-gray-400">Never reached</span>;
  const outcome = OUTCOME_LABELS[value.outcome] || value.outcome || "Contacted";
  return (
    <span>
      {outcome} · {formatWireTime(value.at)}
      {value.source && (
        <span className="block text-xs text-gray-500">{SOURCE_LABELS[value.source] || value.source}</span>
      )}
    </span>
  );
}

/** When the lead is next due, and whether that is a retry or their own request. */
function NextCall({ lead }) {
  if (lead.status === "calling") return <span>Now</span>;
  if (!lead.next_attempt_at) return <span className="text-gray-400">—</span>;
  return (
    <span>
      {formatWireTime(lead.next_attempt_at)}
      {lead.next_attempt_kind === "callback" && (
        <span className="block text-xs text-teal-700">Asked us to call back</span>
      )}
    </span>
  );
}

/**
 * Every lead still in the loop for the clinic, across campaigns — the view
 * for "who are we calling, when, and how did it go last time". Finished
 * leads live in each campaign's drawer, not here.
 */
export default function ReactivationQueueTab({ clinicId }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A load that resolves after the clinic changed belongs to the old clinic.
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      const rows = await fetchQueue(clinicId);
      if (reqRef.current !== reqId) return;
      setLeads(rows);
      setError(null);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      setError(e?.message || "Could not load the queue.");
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Everyone still to be called, across all campaigns: calling now first, then by next call.
        </p>
        <Button color="light" size="sm" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {loading && leads.length === 0 ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : leads.length === 0 ? (
        error ? null : (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
            <p className="text-sm text-gray-600">
              Nobody is waiting to be called. Start a campaign from the Call list tab.
            </p>
          </div>
        )
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Patient</TableHeaderCell>
              <TableHeaderCell>Phone</TableHeaderCell>
              <TableHeaderCell>Campaign</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Last contact</TableHeaderCell>
              <TableHeaderCell>Next call</TableHeaderCell>
              <TableHeaderCell>Attempts</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.map((lead) => {
              const meta = CONTACT_STATUS[lead.status] || {};
              return (
                <TableRow key={lead.contact_id}>
                  <TableCell className="text-sm text-gray-900">{lead.patient_name}</TableCell>
                  <TableCell className="text-sm text-gray-600">{lead.phone}</TableCell>
                  <TableCell className="text-sm text-gray-700">{lead.campaign?.name}</TableCell>
                  <TableCell className="text-sm">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      meta.className || "bg-gray-100 text-gray-700"}`}>
                      {meta.label || lead.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600"><LastContact value={lead.last_contact} /></TableCell>
                  <TableCell className="text-sm text-gray-600"><NextCall lead={lead} /></TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {lead.attempt_no} of {lead.max_attempts}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
